import { Component, OnInit } from '@angular/core';
import { NfceService } from '../../services/nfce.service';
import { AutorizacaoService } from '../../services/autorizacao.service';
import { ConstantsService } from '../../fidelidade/ConstantsService';
import { ImpressaoService } from '../../services/impressao.service';
import { DialogService } from "@progress/kendo-angular-dialog";

enum TipoDeStatus {
  NOVA = 1,
  AUTORIZADA = 2,
  EM_PROCESSAMENTO = 3,
  CANCELADA = 4,
  INUTILIZADA = 5,
  IMPORTADA = 6,
  CONTINGENCIADA = 7,
  DE_CONTINGENCIA = 8,
  DUPLICADA = 9,
  REJEITADA = 10,
  DADOS_INVALIDOS = 11,
  SUCESSO = 12,
  FALHA = 13,
  CONFERENCIA_REJEITOU = 14,
  PRESA_NA_SEFAZ = 15,
  PROBLEMAS_NO_ENVIO = 16,
  ENVIO_INTERROMPIDO = 17,
  PROBLEMA_COMUNICACAO_SEFAZ = 18,
  DENEGADA = 19,
  RECEBIDA = 20,
  PENDENTE_DE_CONCILIACAO = 21,
  REJEITADA_E_PENDENTE_DE_CONCILIACAO = 22,
  PROBLEMAS_NO_ENVIO_EM_CONTINGENCIA = 23,
  PROBLEMA_AO_CANCELAR_NFE = 24
}

interface NFCe {
  id: number;
  numeroNFe: number;
  serie: number;
  pedido?: {
    id: number;
    codigo: string;
    guid: string;
    empresa?: {
      id: number;
      nome?: string;
    };
  };
  nomeDestinatario?: string;
  dataDeEmissao: any;
  valorTotalNFe: number;
  status: number;
  chaveDeAcesso: string;
  motivo?: string;
  numeroProtocolo?: string;
  ultimaAlteracao?: Date;
  dataCriacao?: Date;
}

@Component({
  selector: 'app-grid-nfce',
  templateUrl: './grid-nfce.component.html',
  styleUrls: ['./grid-nfce.component.scss']
})
export class GridNfceComponent implements OnInit {
  dataSource: NFCe[] = [];
  filteredDataSource: NFCe[] = [];
  loading = false;
  empresaId: number;
  usuario: any;
  empresa: any;
  apiUrl = '';
  mensagemErro = '';
  mensagemSucesso = '';
  dialogCancelamento = false;
  justificativaCancelamento = '';
  notaParaCancelar: NFCe | null = null;
  processandoCancelamento = false;
  baixandoZip = false;
  readonly timeZoneDaMaquina = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Filtros e Totalização
  filtro = {
    dataInicio: this.obtenhaDataAtualParaFiltro(),
    dataFim: this.obtenhaDataAtualParaFiltro(),
    status: null as number | null
  };
  totalGeral = 0;

  constructor(
    private nfceService: NfceService,
    private autorizacaoService: AutorizacaoService,
    private constantsService: ConstantsService,
    private impressaoService: ImpressaoService,
    private dialogService: DialogService
  ) {
    this.empresaId = 0;
    this.apiUrl = window.location.origin;
  }

  ngOnInit(): void {
    this.constantsService.empresa$.subscribe(empresa => {
      if (empresa) {
        this.empresa = empresa;
        this.empresaId = empresa.id;
        this.carregarNFCes();
      }
    });

    this.autorizacaoService.usuarioLogado$.subscribe(usuario => {
      this.usuario = usuario;
    });
  }

  carregarNFCes(): void {
    this.loading = true;
    this.mensagemErro = '';
    this.mensagemSucesso = '';

    // Chamada ao servidor passando o objeto de filtro
    this.nfceService.liste(this.filtro)
      .then((data) => {
        this.dataSource = data;
        this.filteredDataSource = data;
        this.calcularTotal();
        this.loading = false;
      })
      .catch((error) => {
        console.error('Erro ao carregar NFCes:', error);
        this.mensagemErro = 'Erro ao carregar as notas fiscais.';
        this.loading = false;
      });
  }

  aplicarFiltros(): void {
    this.carregarNFCes();
  }

  setarDataHoje(): void {
    const hoje = this.obtenhaDataAtualParaFiltro();
    this.filtro.dataInicio = hoje;
    this.filtro.dataFim = hoje;
    this.carregarNFCes();
  }

  calcularTotal(): void {
    this.totalGeral = this.filteredDataSource.reduce((acc, current) => {
      return acc + (current.valorTotalNFe || 0);
    }, 0);
  }

  async imprimir(nfce: NFCe): Promise<void> {
    try {
      this.mensagemErro = '';
      this.mensagemSucesso = '';
      const impressoras = this.empresa.configImpressao.impressoras.filter(i => i.imprimirNFCe);
      if (!impressoras.length) {
        this.mensagemErro = 'Nenhuma impressora configurada para imprimir NFCe';
        return;
      }
      const dadosImpressao = await this.nfceService.obtenhaDadosImpressao(nfce.id);
      if (!dadosImpressao) {
        this.mensagemErro = 'Não foi possível obter os dados da NFCe para impressão.';
        return;
      }
      let imprimiu = false;
      for (const impressora of impressoras) {
        imprimiu = await this.impressaoService.imprimaNfceNaImpressora(dadosImpressao, impressora);
      }
      if (imprimiu) {
        await this.nfceService.marqueImpressa(nfce);
        this.mensagemSucesso = 'NFCe enviada para impressão com sucesso!';
      }
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      this.mensagemErro = 'Erro ao enviar NFCe para impressão.';
    }
  }

  podeReenviar(status: number): boolean {
    const impedem = [
      TipoDeStatus.AUTORIZADA,
      TipoDeStatus.DENEGADA,
      TipoDeStatus.DUPLICADA,
      TipoDeStatus.EM_PROCESSAMENTO,
      TipoDeStatus.CANCELADA,
      TipoDeStatus.INUTILIZADA
    ];
    return !impedem.includes(status);
  }

  async reenviarNota(nfce: NFCe): Promise<void> {
    try {
      const confirmacao = await this.dialogService.open({
        title: "Reenviar Nota Fiscal",
        content: "Deseja realmente reenviar esta nota fiscal?",
        actions: [{ text: "Não" }, { text: "Sim", primary: true }]
      }).result.toPromise();

      if (confirmacao && confirmacao['primary']) {
        this.loading = true;
        await this.nfceService.reenvieNota(nfce.id);
        this.mensagemSucesso = 'Nota fiscal reenviada com sucesso!';
        await this.carregarNFCes();
      }
    } catch (error) {
      this.mensagemErro = error?.message || 'Erro ao reenviar a nota fiscal.';
    } finally {
      this.loading = false;
    }
  }

  podeCancelar(nfce: NFCe): boolean {
    return nfce.status === TipoDeStatus.AUTORIZADA;
  }

  async cancelarNota(nfce: NFCe, event?: any): Promise<void> {
    if (event) { event.stopPropagation(); }
    this.notaParaCancelar = nfce;
    this.justificativaCancelamento = '';
    this.dialogCancelamento = true;
  }

  fecharDialogCancelamento(): void {
    this.dialogCancelamento = false;
    this.notaParaCancelar = null;
  }

  async confirmarCancelamento(): Promise<void> {
    if (!this.justificativaCancelamento || this.justificativaCancelamento.length < 15) {
      this.mensagemErro = "A justificativa deve ter no mínimo 15 caracteres";
      return;
    }
    try {
      this.processandoCancelamento = true;
      await this.nfceService.cancelarNota(this.notaParaCancelar!.id, this.justificativaCancelamento);
      this.mensagemSucesso = "Nota fiscal cancelada com sucesso!";
      this.carregarNFCes();
    } catch (error) {
      this.mensagemErro = typeof error === 'string' ? error : 'Erro ao cancelar nota.';
    } finally {
      this.processandoCancelamento = false;
      this.fecharDialogCancelamento();
    }
  }

  podeImprimir(nfce: NFCe): boolean {
    return nfce.status === TipoDeStatus.AUTORIZADA;
  }

  async downloadXmlNota(nfce: NFCe): Promise<void> {
    try {
      this.mensagemErro = '';
      const blob = await this.nfceService.obtenhaXmlNota(nfce.id);
      this.baixeArquivo(blob, this.obtenhaNomeArquivoXmlNota(nfce));
    } catch (error) {
      this.mensagemErro = 'Erro ao baixar o XML da nota.';
    }
  }

  async downloadXmlDistribuicao(nfce: NFCe): Promise<void> {
    try {
      this.mensagemErro = '';
      const blob = await this.nfceService.obtenhaXmlDistribuicao(nfce.id);
      this.baixeArquivo(blob, this.obtenhaNomeArquivoXmlDistribuicao(nfce));
    } catch (error) {
      this.mensagemErro = 'Erro ao baixar o XML de distribuição.';
    }
  }

  async downloadZipNotasDoFiltroAtual(): Promise<void> {
    try {
      this.baixandoZip = true;
      this.mensagemErro = '';
      this.mensagemSucesso = '';

      const blob = await this.nfceService.obtenhaZipNotas(this.filtro);
      this.baixeArquivo(blob, this.obtenhaNomeArquivoZip());
      this.mensagemSucesso = 'ZIP das notas fiscais gerado com sucesso!';
    } catch (error) {
      const detalhe = typeof error === 'string' ? error : error?.message;
      this.mensagemErro = detalhe || 'Erro ao baixar o ZIP das notas fiscais.';
    } finally {
      this.baixandoZip = false;
    }
  }

  podeDownloadXml(nfce: NFCe): boolean {
    return !!nfce?.id && nfce.status !== TipoDeStatus.AUTORIZADA;
  }

  podeDownloadXmlDistribuicao(nfce: NFCe): boolean {
    return nfce.status === TipoDeStatus.AUTORIZADA;
  }

  obtenhaLinkPedido(nfce: NFCe): string {
    if (!nfce?.pedido?.guid) {
      return '';
    }

    return `/admin/pedidos/detalhes/${nfce.pedido.guid}`;
  }

  private obtenhaDataAtualParaFiltro(): string {
    return this.formateDataParaCampoDeData(new Date());
  }

  private baixeArquivo(blob: Blob, nomeArquivo: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private obtenhaNomeArquivoXmlNota(nfce: NFCe): string {
    return `${nfce.chaveDeAcesso || 'nfce-' + nfce.id}-nfe.xml`;
  }

  private obtenhaNomeArquivoXmlDistribuicao(nfce: NFCe): string {
    return `${nfce.chaveDeAcesso || 'nfce-' + nfce.id}-procNFe.xml`;
  }

  private obtenhaNomeArquivoZip(): string {
    const partesPeriodo: string[] = [];

    if (this.filtro.dataInicio) {
      partesPeriodo.push(this.filtro.dataInicio);
    }

    if (this.filtro.dataFim && this.filtro.dataFim !== this.filtro.dataInicio) {
      partesPeriodo.push(this.filtro.dataFim);
    }

    return `notas-fiscais${partesPeriodo.length ? '-' + partesPeriodo.join('-a-') : ''}.zip`;
  }

  private formateDataParaCampoDeData(data: Date, timeZone = this.timeZoneDaMaquina): string {
    if (!timeZone) {
      return this.formateDataUsandoHorarioLocal(data);
    }

    const partes = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(data);

    const ano = partes.find((parte) => parte.type === 'year')?.value;
    const mes = partes.find((parte) => parte.type === 'month')?.value;
    const dia = partes.find((parte) => parte.type === 'day')?.value;

    if (!ano || !mes || !dia) {
      return this.formateDataUsandoHorarioLocal(data);
    }

    return `${ano}-${mes}-${dia}`;
  }

  private formateDataUsandoHorarioLocal(data: Date): string {
    const ano = data.getFullYear();
    const mes = `${data.getMonth() + 1}`.padStart(2, '0');
    const dia = `${data.getDate()}`.padStart(2, '0');

    return `${ano}-${mes}-${dia}`;
  }
}
